import { ServiceRequest, Patient } from '@medplum/fhirtypes';
import { getPatientOrPractitionerName } from './common-helpers';
import { getConsentStatusForOrder } from './order-utils';
import {
  ORDER_STATUS_I18N_KEYS,
  OrderStatusI18nKey,
  ORDER_SUBSTATE_I18N_KEYS,
  OrderSubstateI18nKey,
} from '../constants/order.consts';
import { OrderSubState } from '../lims-types';
import { ConsentStatus } from '@/types/consent.types';
import { ARKON } from '@consts/systems.consts';

export const OrderUtils = {
  getOrderId: (order: ServiceRequest): string => {
    return order.identifier?.[0]?.value || order.id || '';
  },

  getPatientName: (
    order: ServiceRequest,
    patients: Map<string, Patient>,
    fallback: string
  ): string => {
    const patientId = order.subject?.reference?.replace('Patient/', '');
    if (!patientId) {
      return fallback;
    }

    const patient = patients.get(patientId);
    if (!patient) {
      return fallback;
    }

    return getPatientOrPractitionerName(patient);
  },

  getTestName: (order: ServiceRequest, fallback: string): string => {
    const coding = order.code?.coding?.[0];
    return coding?.display || coding?.code || fallback;
  },

  getStatusName: (status: string | undefined): OrderStatusI18nKey => {
    if (!status) {
      return 'orderList.statuses.unknown';
    }
    const key =
      ORDER_STATUS_I18N_KEYS[status as keyof typeof ORDER_STATUS_I18N_KEYS];
    return key || 'orderList.statuses.unknown';
  },

  getSubStateName: (
    order: ServiceRequest
  ): OrderSubstateI18nKey | 'orderList.unknown' => {
    const subStateTag = order.meta?.tag?.find(
      (tag) => tag.system === ARKON.SD.LAB_ORDER_SUBSTATE
    );
    const code = subStateTag?.code as OrderSubState | undefined;
    if (code && ORDER_SUBSTATE_I18N_KEYS[code]) {
      return ORDER_SUBSTATE_I18N_KEYS[code];
    }
    return 'orderList.unknown';
  },

  getSubStateStatus: (order: ServiceRequest): string => {
    const subStateTag = order.meta?.tag?.find(
      (tag) => tag.system === ARKON.SD.LAB_ORDER_SUBSTATE
    );
    const code = subStateTag?.code as OrderSubState | undefined;
    if (code) {
      return code;
    }
    return 'unknown';
  },

  getConsentStatus: (
    order: ServiceRequest,
    consentStatuses: Map<string, ConsentStatus>
  ): ConsentStatus => {
    return getConsentStatusForOrder(order, consentStatuses);
  },
};
